import type { IndexPageSize } from "@/components/ads-assistant/useAdsAssistantIndexFilters";

type Props = {
  total: number | null;
  page: number;
  pageSize: IndexPageSize;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: IndexPageSize) => void;
  disabled?: boolean;
};

export function AdsAssistantIndexPagination({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  disabled,
}: Props) {
  const count = total ?? 0;
  const pageCount = count > 0 ? Math.ceil(count / pageSize) : 1;
  const safePage = Math.min(page, Math.max(0, pageCount - 1));
  const rangeStart = count === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = Math.min((safePage + 1) * pageSize, count);
  const canPrev = safePage > 0;
  const canNext = count > 0 && safePage < pageCount - 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-2 dark:border-gray-600">
      <p className="text-[11px] text-gray-600 dark:text-gray-400">
        {count === 0 ? (
          "No posts"
        ) : (
          <>
            Showing <span className="tabular-nums font-medium text-gray-800 dark:text-gray-200">{rangeStart}</span>
            –<span className="tabular-nums font-medium text-gray-800 dark:text-gray-200">{rangeEnd}</span> of{" "}
            <span className="tabular-nums font-medium text-gray-800 dark:text-gray-200">{count}</span>
          </>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-400">
          Per page
          <select
            className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-xs dark:border-gray-600 dark:bg-zinc-900"
            value={pageSize}
            disabled={disabled}
            onChange={(e) => onPageSizeChange(Number(e.target.value) as IndexPageSize)}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={disabled || !canPrev}
            onClick={() => onPageChange(safePage - 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
          >
            Prev
          </button>
          <span className="px-1 text-[11px] tabular-nums text-gray-600 dark:text-gray-400">
            {count === 0 ? "—" : `${safePage + 1} / ${pageCount}`}
          </span>
          <button
            type="button"
            disabled={disabled || !canNext}
            onClick={() => onPageChange(safePage + 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
